import {
  ActionMenu,
  ActionMenuItem,
  ActionMenuItemSpacer,
} from '@frontapp/ui-kit';
import { useState } from 'react';
import { useNotifications } from 'reapop';
import Ellipsis from './Ellipsis';
import CopyActionMenuItem from './menuItems/CopyActionMenuItem';
import GotoActionMenuItem from './menuItems/GotoActionMenuItem';
import { type Organization, type Site, levelEmoji } from './types';
import { askIfAbort } from './utils/askIfAbort';
import { editUploadStorageSettings } from './utils/editUploadStorageSettings';
import { useInternalClient } from './utils/useInternalClient';

type Props = {
  site: Site;
  organization?: Organization;
};

function SiteInfo({ site, organization }: Props) {
  const client = useInternalClient();
  const { notify } = useNotifications();
  const [deactivated, setDeactivated] = useState(site.deactivated);
  const [usageBlocked, setUsageBlocked] = useState(site.usage_blocked);

  async function handleUploadStorageSettings() {
    await editUploadStorageSettings({
      siteId: site.id,
      uploadStorageSettings: site.upload_storage_settings,
    });
  }

  async function handleReactivateProject() {
    try {
      await client.reactivateProject(site.id);
      setDeactivated(false);
      setUsageBlocked(false);

      notify('Project successfully reactivated!', 'success');
    } catch (e) {
      notify('Something went wrong...', 'error');
    }
  }

  async function handleDeactivateProject() {
    if (
      await askIfAbort(
        `Are you sure you want to deactivate project "${site.name}"?`,
        'Yes, deactivate project',
      )
    ) {
      return;
    }

    try {
      await client.deactivateProject(site.id);
      setDeactivated(true);

      notify('Project successfully deactivated!', 'success');
    } catch (e) {
      notify('Something went wrong...', 'error');
    }
  }

  const orgTags = [
    organization && organization.appointed_organizations.length > 0
      ? 'with mandate'
      : null,
    organization?.referrer_organization
      ? `referred by ${organization.referrer_organization.name}`
      : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <tr>
      <td>
        <Ellipsis
          content={`${levelEmoji[site.plan.support_level]} ${site.name}`}
        />
      </td>
      <td>
        <Ellipsis
          content={
            site.current_account.relationship === 'collaborator'
              ? 'Collaborator'
              : site.current_account.relationship === 'sso_user'
                ? `SSO user 🔑 (${site.owner?.title})`
                : site.current_account.relationship === 'owner'
                  ? 'Project Owner'
                  : `${site.owner?.title} ${
                      site.owner?.is_agency_partner
                        ? '(Partner 🤝)'
                        : `(Org 🏢${orgTags ? ` ${orgTags}` : ''})`
                    }`
          }
        />
      </td>
      <td>
        <ActionMenu width={280}>
          <GotoActionMenuItem
            title={
              site.current_account.relationship === 'owner'
                ? 'Enter project as account/owner'
                : site.current_account.relationship === 'sso_user'
                  ? 'Enter project as SSO user'
                  : 'Enter project as account'
            }
            url={site.current_account.site_enter_url}
          />

          {site.owner && <ActionMenuItemSpacer />}

          {site.owner && (
            <CopyActionMenuItem
              title="Owner"
              value={`${site.owner.title}${
                site.owner.is_agency_partner ? ' (Partner 🤝)' : ''
              }`}
            />
          )}

          {site.owner && (
            <CopyActionMenuItem
              title="Owner ID"
              isId={true}
              value={site.owner.id}
            />
          )}

          {organization?.appointed_organizations?.map((agency) => (
            <CopyActionMenuItem
              key={agency.id}
              title="Partner 🤝"
              value={agency.name}
            />
          ))}

          {organization?.referrer_organization && (
            <CopyActionMenuItem
              title="Referred by"
              value={organization.referrer_organization.name}
            />
          )}

          {site.owner && (
            <GotoActionMenuItem
              title="Enter project as owner"
              url={site.owner.site_enter_url}
            />
          )}
          {site.owner && (
            <GotoActionMenuItem
              title="Enter dashboard as owner"
              url={site.owner.dashboard_enter_url}
            />
          )}

          <ActionMenuItemSpacer />

          <CopyActionMenuItem title="Site ID" isId={true} value={site.id} />
          <CopyActionMenuItem
            title="Primary env ID"
            isId={true}
            value={site.primary_environment_id}
          />
          <CopyActionMenuItem
            title="Domain"
            value={`${site.internal_subdomain}.admin.datocms.com`}
          />

          {site.plan.type === 'per_site_pricing' && <ActionMenuItemSpacer />}

          {site.plan.type === 'per_site_pricing' && (
            <CopyActionMenuItem
              title="Site plan ID"
              isId={true}
              value={site.plan.id}
            />
          )}
          {site.plan.type === 'per_site_pricing' && (
            <CopyActionMenuItem title="Site plan name" value={site.plan.name} />
          )}

          <ActionMenuItemSpacer />

          <ActionMenuItem iconName="Edit" onClick={handleUploadStorageSettings}>
            Edit upload storage settings...
          </ActionMenuItem>

          <ActionMenuItemSpacer />

          {deactivated || usageBlocked ? (
            <ActionMenuItem
              iconName="CheckmarkCircle"
              onClick={handleReactivateProject}
            >
              Reactivate project
            </ActionMenuItem>
          ) : (
            <ActionMenuItem iconName="Close" onClick={handleDeactivateProject}>
              Deactivate project
            </ActionMenuItem>
          )}
        </ActionMenu>
      </td>
    </tr>
  );
}

export default SiteInfo;
